import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, Download, Lock, Search, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { listEbooks } from "@/lib/ebooks.functions";
import { authedFetch } from "@/lib/authed-fetch";

export const Route = createFileRoute("/_authenticated/dashboard/ebooks")({
  head: () => ({
    meta: [
      { title: "E-Books Library | XAU Pro Academy" },
      {
        name: "description",
        content:
          "Download premium SMC and gold-trading e-books from the XAU Pro Academy library. Free sample for all members, full library for paid plans.",
      },
    ],
  }),
  component: EbooksPage,
});

type Ebook = {
  id: string;
  title: string;
  description: string | null;
  cover_url: string | null;
  pdf_url: string | null;
  is_free_sample: boolean;
  position: number;
};

function EbooksPage() {
  const { profile } = useAuth();
  const [ebooks, setEbooks] = useState<Ebook[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const tier = profile?.tier ?? "free";
  const isPaid = tier !== "free";

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        // Fetched server-side: pdf_url is omitted for free-tier users so
        // premium download links never reach the browser.
        const data = await listEbooks({ fetch: authedFetch });
        if (!cancelled) {
          setEbooks(data ?? []);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();

    const channel = supabase
      .channel("ebooks-list")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "ebooks" },
        () => fetchAll(),
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ebooks;
    return ebooks.filter((b) => b.title.toLowerCase().includes(q));
  }, [ebooks, search]);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-10">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:mb-10 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold-muted/40 px-3 py-1 text-xs font-medium text-gold">
            <BookOpen className="h-3.5 w-3.5" />
            Library
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            E-Books <span className="text-gradient-gold">Library</span>
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">
            Curated SMC and gold-trading playbooks. Free members get our starter
            sample. Paid plans unlock the full collection.
          </p>
        </div>

        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title..."
            className="pl-9"
          />
        </div>
      </div>

      {loading ? (
        <BookGridSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState hasBooks={ebooks.length > 0} />
      ) : (
        <div className="grid grid-cols-2 gap-x-5 gap-y-10 sm:gap-x-6 sm:gap-y-12 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((book) => (
            <EbookCard key={book.id} book={book} isPaid={isPaid} />
          ))}
        </div>
      )}
    </div>
  );
}

function EbookCard({ book, isPaid }: { book: Ebook; isPaid: boolean }) {
  const canDownload = isPaid || book.is_free_sample;
  const [imgError, setImgError] = useState(false);

  return (
    <div className="group flex h-full flex-col">
      {/* Book cover */}
      <div className="relative [perspective:1200px]">
        <div
          className={[
            "relative aspect-[3/4] w-full overflow-hidden rounded-md",
            "bg-gradient-to-br from-slate-elevated to-slate-deep",
            "shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7),0_4px_12px_-4px_rgba(0,0,0,0.4)]",
            "transition-all duration-300 ease-out",
            "group-hover:-translate-y-2 group-hover:scale-[1.03]",
            "group-hover:shadow-[0_25px_50px_-12px_rgba(212,175,55,0.35),0_10px_25px_-8px_rgba(0,0,0,0.6)]",
            // Spine highlight on the left edge
            "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-10 before:w-[6px]",
            "before:bg-[linear-gradient(90deg,rgba(0,0,0,0.45),rgba(255,255,255,0.08)_55%,rgba(0,0,0,0.25))]",
            // Subtle top sheen
            "after:pointer-events-none after:absolute after:inset-x-0 after:top-0 after:h-1/3",
            "after:bg-[linear-gradient(180deg,rgba(255,255,255,0.08),transparent)]",
          ].join(" ")}
        >
          {book.cover_url && !imgError ? (
            <img
              src={book.cover_url}
              alt={`${book.title} cover`}
              loading="lazy"
              decoding="async"
              onError={() => setImgError(true)}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <BookOpen className="h-14 w-14 text-gold/40" />
            </div>
          )}

          {/* Free Sample badge */}
          {book.is_free_sample && (
            <div className="absolute right-2 top-2 z-20 inline-flex items-center gap-1 rounded-full border border-gold/60 bg-background/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold shadow-[0_0_14px_rgba(212,175,55,0.45)] backdrop-blur-md">
              <Sparkles className="h-3 w-3" />
              Free
            </div>
          )}

          {/* Lock overlay */}
          {!canDownload && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/65 backdrop-blur-[3px]">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/50 bg-background/80 shadow-[0_0_20px_rgba(212,175,55,0.35)]">
                <Lock className="h-5 w-5 text-gold" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="mt-5 flex flex-1 flex-col">
        <div>
          <h3 className="line-clamp-2 min-h-[2.75rem] font-display text-base font-semibold leading-snug">
            {book.title}
          </h3>
          <p className="mt-1.5 line-clamp-3 min-h-[3.15rem] text-xs text-muted-foreground sm:min-h-[3.75rem] sm:text-sm">
            {book.description ?? ""}
          </p>
        </div>

        <div className="mt-auto pt-4">
          {canDownload ? (
            <Button
              asChild
              size="sm"
              className="w-full bg-gradient-gold text-gold-foreground shadow-gold hover:opacity-90"
            >
              <a
                href={book.pdf_url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </a>
            </Button>
          ) : (
            <div className="space-y-2">
              <Button disabled size="sm" variant="secondary" className="w-full">
                <Lock className="mr-2 h-4 w-4" />
                Locked
              </Button>
              <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
                <Link
                  to="/pricing"
                  className="font-medium text-gold hover:underline"
                >
                  Upgrade
                </Link>{" "}
                to unlock the full library
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BookGridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-x-5 gap-y-10 sm:gap-x-6 sm:gap-y-12 md:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex flex-col">
          <div className="relative aspect-[3/4] w-full overflow-hidden rounded-md bg-slate-elevated shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)]">
            <div className="absolute inset-0 shimmer-overlay" />
            <div className="absolute inset-y-0 left-0 w-[6px] bg-[linear-gradient(90deg,rgba(0,0,0,0.45),rgba(255,255,255,0.06))]" />
          </div>
          <div className="mt-5 space-y-2">
            <div className="h-4 w-3/4 overflow-hidden rounded bg-slate-elevated">
              <div className="h-full w-full shimmer-overlay" />
            </div>
            <div className="h-3 w-1/2 overflow-hidden rounded bg-slate-elevated">
              <div className="h-full w-full shimmer-overlay" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ hasBooks }: { hasBooks: boolean }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center rounded-xl border border-dashed border-border/60 bg-card/40 p-10 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gold-muted/40">
        <Sparkles className="h-6 w-6 text-gold" />
      </div>
      <h2 className="font-display text-lg font-semibold">
        {hasBooks ? "No matches found" : "Library coming soon"}
      </h2>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">
        {hasBooks
          ? "Try a different search term to find the right e-book."
          : "We're preparing the first wave of premium SMC playbooks. Check back shortly."}
      </p>
    </div>
  );
}
